import { useCallback, useContext, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { HashedPermission, ORG_ROOT } from "@songtradr-one/shared-common";
import { Api } from "../../api/api";
import { AppStorage } from "../../helpers/app-storage";
import { getApollo } from "../../helpers/get-apollo";
import { AppLayoutConfig } from "../../layouts/App.Parts";
import {
  AppContext,
  CurrentOrg,
  ProviderProps,
  User,
  hasPermission,
  hasPermissions,
} from "./AppContext.Parts";

export const AppContextProvider = ({ children, appConfig }: ProviderProps) => {
  const [user, _setUser] = useState<User>();
  const [currentOrg, _setCurrentOrg] = useState<CurrentOrg>();
  const { client } = getApollo();

  const _setOrgId = useCallback((orgId?: string) => {
    AppStorage.currentOrgId = orgId ?? null;
    Sentry.setTag("org_id", orgId ?? null);
    updateTemporaryStudioCookie();
  }, []);

  /**@todo remove with temporary studio bridge */
  const updateTemporaryStudioCookie = () => {
    if (window.location.origin.includes("studio")) {
      const orgId =
        AppStorage.currentOrgId === ORG_ROOT
          ? "6315c04cc80bc8cf4fa3be2a" // Default to Songtradr Root Org on Studio
          : AppStorage.currentOrgId;
      document.cookie = `portal-organisationId=${orgId}; domain=.${window.location.host}; path=/;`;
    }
  };

  const setUser = (user?: User) => {
    try {
      _setOrgId(user?.orgId);
      Sentry.setUser(user ? { id: user.id } : null);
      _setUser(user);
    } catch (error) {
      console.error("Error setting user in AppContextProvider:", error);
    }
  };

  const setCurrentOrg = (newCurrentOrg: CurrentOrg, effectiveUser?: User) => {
    const orgUser = effectiveUser ?? user;
    if (!orgUser) throw new Error("User must be set before setting org");

    if (
      !orgUser.inOrgs.some((org) => org.id === newCurrentOrg.id) &&
      !orgUser.permissions.includes(HashedPermission.SYSTEM_ROOT)
    )
      throw new Error("User does not belong to this org");

    AppStorage.workspace = newCurrentOrg.workspace || null;
    _setOrgId(newCurrentOrg.id);
    _setCurrentOrg(newCurrentOrg);
  };

  const refreshContext = useCallback(
    async (matchOrgId?: string) => {
      // Only refresh context if org id matches current org id
      if (matchOrgId && currentOrg?.id !== matchOrgId) return;

      // Reset the Apollo Client store to clear any cached data and to reload all current data
      try {
        await client.resetStore();
      } catch (error) {
        console.error("Error resetting Apollo Client store:", error);
      }
    },
    [client, currentOrg?.id],
  );

  const switchOrg = useCallback(
    async (newOrgId: string) => {
      if (newOrgId === currentOrg?.id) return;
      if (!user) throw new Error("User must be set before setting org");

      if (
        !user.inOrgs.some((org) => org.id === newOrgId) &&
        !user.permissions.includes(HashedPermission.SYSTEM_ROOT)
      )
        throw new Error("User does not belong to this org");

      _setOrgId(newOrgId);

      await refreshContext();
    },
    [currentOrg?.id, user, refreshContext, _setOrgId],
  );

  const hasPermission: hasPermission = (permission) => {
    return hasAnyPermission([permission]);
  };

  const hasAnyPermission = useCallback<hasPermissions>(
    (permissions) => {
      if (permissions.length === 0) return true;
      const permSet = new Set(user?.permissions);
      return permissions.some((p) => permSet.has(p));
    },
    [user?.permissions],
  );

  const hasAllPermissions = useCallback<hasPermissions>(
    (permissions) => {
      if (permissions.length === 0) return true;
      const userPermSet = new Set(user?.permissions);
      return permissions.every((p) => userPermSet.has(p));
    },
    [user?.permissions],
  );

  // Create a dynamic appConfig that includes the organization's analytics settings
  const dynamicAppConfig: AppLayoutConfig = {
    ...appConfig,
    analytics: {
      ...appConfig.analytics,
      heap: {
        enabled: currentOrg?.metadata?.analytics?.heap?.enabled ?? false,
        id: appConfig.analytics?.heap?.id ?? "",
      },
    },
    branding: {
      logo: currentOrg?.metadata?.assets?.logo ?? undefined,
      background: currentOrg?.metadata?.assets?.background ?? undefined,
    },
  };

  // Sync org across tabs and windows: react immediately when another context
  // writes a new currentOrgId to localStorage.
  useEffect(() => {
    return AppStorage.onCurrentOrgIdChange((newOrgId) => {
      if (newOrgId && newOrgId !== currentOrg?.id) {
        void switchOrg(newOrgId);
      }
    });
  }, [currentOrg?.id, switchOrg]);

  // Configure API client
  useEffect(() => {
    if (appConfig.coreBackendUrl) {
      Api.defaults.baseURL = appConfig.coreBackendUrl;
    }

    if (appConfig.appId) {
      Api.defaults.headers.common = {
        ...Api.defaults.headers.common,
        "x-app-id": appConfig.appId,
      };
    }
  }, [appConfig.coreBackendUrl, appConfig.appId]);

  return (
    <AppContext.Provider
      value={{
        user,
        currentOrg,
        setUser,
        setCurrentOrg,
        switchOrg,
        refreshContext,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        appConfig: dynamicAppConfig,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within a AppContextProvider");
  }
  return context;
};
